/**
 * MobilePreviewUrlBar Component
 * 
 * A URL dropdown bar for mobile that opens upwards.
 * Shows available pages/routes with same functionality as PreviewUrlBar.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { PublicFile } from '../../types/AppPanel';

interface MobilePreviewUrlBarProps {
  /** Base URL for the preview */
  baseUrl?: string;
  /** Available public files/pages */
  publicFiles?: PublicFile[];
  /** Currently selected path */
  currentPath?: string;
  /** Callback when path changes */
  onPathChange?: (path: string) => void;
  /** Callback to refresh preview */
  onRefresh?: () => void;
  /** Callback to open in new tab */
  onOpenExternal?: () => void;
}

export default function MobilePreviewUrlBar({
  baseUrl,
  publicFiles,
  currentPath = '/',
  onPathChange,
  onRefresh,
  onOpenExternal,
}: MobilePreviewUrlBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract routes from public files
  const routes = useMemo(() => {
    if (!publicFiles || publicFiles.length === 0) {
      // Default route when no files detected
      return [{ path: '/', label: '/' }];
    }

    const routeSet = new Set<string>();
    routeSet.add('/'); // Always include root (index.html)

    publicFiles.forEach(file => {
      if (file.type === 'html' || file.name.endsWith('.html')) {
        let route = file.path.replace(/^public\/?/, '/');
        
        if (route === '/index.html' || route.endsWith('/index.html')) {
          route = route.replace(/\/?index\.html$/, '/');
        }
        
        if (!route.startsWith('/')) route = '/' + route;
        route = route.replace(/\/+/g, '/');
        if (route !== '/' && route.endsWith('/')) {
          route = route.slice(0, -1);
        }
        
        routeSet.add(route);
      }
    });

    return Array.from(routeSet)
      .sort((a, b) => {
        if (a === '/') return -1;
        if (b === '/') return 1;
        return a.localeCompare(b);
      })
      .map(path => ({ 
        path, 
        label: path === '/' ? '/' : path.replace(/\.html$/, '')
      }));
  }, [publicFiles]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle route selection
  const handleSelectRoute = (path: string) => {
    setIsOpen(false);
    onPathChange?.(path);
  };

  // Get current label
  const currentLabel = useMemo(() => {
    const route = routes.find(r => r.path === currentPath);
    return route?.label || currentPath;
  }, [routes, currentPath]);

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Dropdown that opens upward */}
      {isOpen && routes.length > 0 && (
        <div className="
          absolute bottom-full left-0 right-0 mb-2 z-[9999]
          bg-[#0c121c] backdrop-blur-xl
          border border-white/10 rounded-lg
          shadow-xl shadow-black/50
          max-h-[200px] overflow-y-auto
          py-1
        ">
          {routes.map(({ path, label }) => {
            const isActive = path === currentPath;
            return (
              <button
                key={path}
                type="button"
                onClick={() => handleSelectRoute(path)}
                className={`
                  w-full flex items-center justify-between gap-3 h-[30px]
                  p-3 text-left
                  transition-colors duration-100
                  ${isActive
                    ? 'bg-[#208AEC]/25 text-white'
                    : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <span className="font-mono text-sm">{label}</span>
                {isActive && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#208AEC] flex-shrink-0">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* URL Bar Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center justify-between gap-2 w-full
          bg-[#1a2436] rounded-full border border-white/10
          h-[40px] px-4
          transition-all duration-200
        "
        style={{ backgroundColor: '#1a2436' }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Browser Icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white/50 flex-shrink-0">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
            <circle cx="6.5" cy="6" r="1" fill="currentColor"/>
            <circle cx="9.5" cy="6" r="1" fill="currentColor"/>
          </svg>

          {/* Current Path */}
          <span className="text-sm text-white/80 font-mono truncate">
            {currentLabel}
          </span>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Refresh Button */}
          {onRefresh && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
              className="p-1.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white/80 transition-colors"
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Open External Button */}
          {onOpenExternal && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenExternal();
              }}
              className="p-1.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white/80 transition-colors"
              title="Open in new tab"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Chevron */}
          <div className='p-1.5'>
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none"
              className={`text-white/50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            >
              <path 
                d="M6 9l6 6 6-6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </button>
    </div>
  );
}

